import React, { useState, useEffect, useCallback, useMemo, useRef, createContext, useContext } from 'react';

import classNames from 'classnames';

// Render strategy constants
export const RenderStrategyOnVisit = 'on-visit';
export const RenderStrategyOnFirstVisit = 'on-first-visit';
export const RenderStrategyAlways = 'always';

// Context for internal tab state management
const TabsContext = createContext(null);

/**
 * Hook to access tabs context (for advanced use cases)
 */
export const useTabsContext = () => {
    const context = useContext(TabsContext);
    if (!context) {
        throw new Error('useTabsContext must be used within a Tabs component');
    }
    return context;
};

/**
 * Tabs Component
 *
 * A robust tab navigation component with lazy content rendering via render functions.
 *
 * @param {Object} props
 * @param {Array} props.tabs - Array of tab configuration objects
 * @param {string} props.tabs[].id - Unique tab identifier
 * @param {string} props.tabs[].title - Tab button label
 * @param {Function} props.tabs[].content - Render function () => ReactNode for lazy content
 * @param {boolean} [props.tabs[].disabled] - Whether tab is disabled
 * @param {boolean} [props.tabs[].hidden] - Whether tab is hidden
 * @param {boolean} [props.tabs[].active] - Whether tab should be initially active
 * @param {string} [props.tabs[].renderStrategy] - Per-tab render strategy override
 * @param {Array} [props.actions] - Contextual actions for the tab bar
 * @param {string} [props.activeTab] - Controlled active tab ID
 * @param {string} [props.renderStrategy='always'] - Default render strategy
 * @param {boolean} [props.defaultToFirstTab=true] - Auto-select first enabled tab
 * @param {boolean} [props.horizontal=true] - Tab orientation
 * @param {boolean} [props.maxScreenHeight=false] - Use full available height
 * @param {Function} [props.onTabActivate] - Callback when active tab changes
 */
const Tabs = ({
    tabs: initialTabs = [],
    actions = [],
    activeTab: controlledActiveTab,
    renderStrategy = RenderStrategyAlways,
    defaultToFirstTab = true,
    horizontal = true,
    maxScreenHeight = false,
    onTabActivate,
}) => {
    // Determine if component is controlled
    const isControlled = controlledActiveTab !== undefined && controlledActiveTab !== null;

    // Filter out hidden tabs
    const tabs = useMemo(() => initialTabs.filter((tab) => !tab.hidden), [initialTabs]);

    // Compute initial tab ID
    const getInitialTabId = useCallback(() => {
        /**
         * Priority:
         * 1. Controlled value
         * 2. Tab with active: true
         * 3. First enabled tab
         */
        if (isControlled && tabs.some((tab) => tab.id === controlledActiveTab && !tab.disabled)) {
            return controlledActiveTab;
        }

        const activeTab = tabs.find((tab) => tab.active && !tab.disabled);
        if (activeTab) {
            return activeTab.id;
        }

        if (defaultToFirstTab) {
            const firstEnabled = tabs.find((tab) => !tab.disabled);
            return firstEnabled?.id ?? null;
        }

        return null;
    }, [controlledActiveTab, tabs, defaultToFirstTab, isControlled]);

    // Internal state for uncontrolled mode
    const [internalActiveTabId, setInternalActiveTabId] = useState(getInitialTabId);

    // Track which tabs have been rendered (for on-first-visit strategy)
    const [visitedTabIds, setVisitedTabIds] = useState(() => {
        const initialId = getInitialTabId();
        return initialId ? new Set([initialId]) : new Set();
    });

    // Track if this is the initial render (to avoid double-firing onTabActivate)
    const isInitialMount = useRef(true);

    // Determine the actual active tab ID
    const activeTabId = isControlled ? controlledActiveTab : internalActiveTabId;

    // Find the active tab object
    const activeTab = useMemo(() => tabs.find((tab) => tab.id === activeTabId) ?? null, [tabs, activeTabId]);

    // Sync internal state when controlled value changes
    useEffect(() => {
        if (isControlled && controlledActiveTab !== internalActiveTabId) {
            setInternalActiveTabId(controlledActiveTab);
        }
    }, [isControlled, controlledActiveTab, internalActiveTabId]);

    // Handle initial tab selection for uncontrolled mode when tabs change
    useEffect(() => {
        if (!isControlled && !activeTabId && defaultToFirstTab) {
            const firstEnabled = tabs.find((tab) => !tab.disabled);
            if (firstEnabled) {
                setInternalActiveTabId(firstEnabled.id);
            }
        }
    }, [isControlled, activeTabId, tabs, defaultToFirstTab]);

    // Track visited tabs and notify parent of tab changes
    useEffect(() => {
        if (!activeTab) return;

        // Track tab for on-first-visit strategy
        setVisitedTabIds((prev) => {
            if (prev.has(activeTab.id)) return prev;
            const next = new Set(prev);
            next.add(activeTab.id);
            return next;
        });

        // Notify parent (skip initial mount to avoid double-firing)
        if (!isInitialMount.current && typeof onTabActivate === 'function') {
            onTabActivate(activeTab.id);
        }

        isInitialMount.current = false;
    }, [activeTab, onTabActivate]);

    // Handle tab activation
    const handleTabChange = useCallback(
        (tabId) => {
            const tab = tabs.find((t) => t.id === tabId);
            if (!tab || tab.disabled) return;

            if (!isControlled) {
                setInternalActiveTabId(tabId);
            }

            // Always call onTabActivate for controlled mode
            if (isControlled && typeof onTabActivate === 'function') {
                onTabActivate(tabId);
            }
        },
        [tabs, isControlled, onTabActivate],
    );

    // Handle keyboard navigation
    const handleKeyDown = useCallback(
        (event) => {
            const enabledTabs = tabs.filter((tab) => !tab.disabled);
            const currentIndex = enabledTabs.findIndex((tab) => tab.id === activeTabId);

            if (currentIndex === -1) return;

            let nextIndex = currentIndex;
            const isHorizontal = horizontal;

            switch (event.key) {
                case 'ArrowRight':
                    if (isHorizontal) {
                        nextIndex = (currentIndex + 1) % enabledTabs.length;
                    }
                    break;
                case 'ArrowLeft':
                    if (isHorizontal) {
                        nextIndex = (currentIndex - 1 + enabledTabs.length) % enabledTabs.length;
                    }
                    break;
                case 'ArrowDown':
                    if (!isHorizontal) {
                        nextIndex = (currentIndex + 1) % enabledTabs.length;
                    }
                    break;
                case 'ArrowUp':
                    if (!isHorizontal) {
                        nextIndex = (currentIndex - 1 + enabledTabs.length) % enabledTabs.length;
                    }
                    break;
                case 'Home':
                    nextIndex = 0;
                    break;
                case 'End':
                    nextIndex = enabledTabs.length - 1;
                    break;
                default:
                    return;
            }

            if (nextIndex !== currentIndex) {
                event.preventDefault();
                handleTabChange(enabledTabs[nextIndex].id);
            }
        },
        [tabs, activeTabId, horizontal, handleTabChange],
    );

    // Determine if a tab should render its content
    const shouldRenderTab = useCallback(
        (tab) => {
            const strategy = tab.renderStrategy || renderStrategy;

            switch (strategy) {
                case RenderStrategyAlways:
                    return true;
                case RenderStrategyOnVisit:
                    return activeTabId === tab.id;
                case RenderStrategyOnFirstVisit:
                    return visitedTabIds.has(tab.id);
                default:
                    return true;
            }
        },
        [renderStrategy, activeTabId, visitedTabIds],
    );

    // Render tab content - handles both render functions and direct elements
    const renderTabContent = useCallback((tab) => {
        if (typeof tab.content === 'function') {
            return tab.content();
        }
        // Fallback for any legacy direct JSX (shouldn't happen after migration)
        return tab.content;
    }, []);

    // Context value for compound components
    const contextValue = useMemo(
        () => ({
            activeTabId,
            tabs,
            actions,
            horizontal,
            maxScreenHeight,
            activeTab,
            handleTabChange,
            handleKeyDown,
            shouldRenderTab,
            renderTabContent,
        }),
        [activeTabId, tabs, actions, horizontal, maxScreenHeight, activeTab, handleTabChange, handleKeyDown, shouldRenderTab, renderTabContent],
    );

    // Container class names
    const containerClasses = classNames('tw-flex', {
        'tw-flex-col tw-gap-6': horizontal,
        'tw-items-start tw-gap-6': !horizontal,
        'tw-h-full': maxScreenHeight,
    });

    return (
        <TabsContext.Provider value={contextValue}>
            <div className={containerClasses}>
                <TabList />
                <TabPanels />
            </div>
        </TabsContext.Provider>
    );
};

/**
 * TabList - Renders the tab navigation buttons
 */
const TabList = () => {
    const { tabs, actions, horizontal, handleKeyDown, activeTab } = useTabsContext();

    const navClasses = classNames('in-nav', {
        horizontal,
        'tw-sticky tw-top-24 tw-basis-full md:tw-basis-2/5': !horizontal,
    });

    return (
        <div className={navClasses} role="tablist" aria-orientation={horizontal ? 'horizontal' : 'vertical'} onKeyDown={handleKeyDown}>
            {tabs.map((tab) => (
                <TabTrigger key={tab.id} tab={tab} />
            ))}
            <TabActions actions={actions} activeTab={activeTab} />
        </div>
    );
};

/**
 * TabTrigger - Individual tab button with proper accessibility
 */
const TabTrigger = ({ tab }) => {
    const { activeTabId, handleTabChange } = useTabsContext();
    const isActive = activeTabId === tab.id;

    const handleClick = useCallback(() => {
        handleTabChange(tab.id);
    }, [handleTabChange, tab.id]);

    return (
        <button
            className={classNames('in-nav__item', {
                active: isActive,
                inactive: !isActive,
                disabled: tab.disabled,
            })}
            id={`${tab.id}-tab`}
            disabled={tab.disabled}
            type="button"
            role="tab"
            tabIndex={isActive ? 0 : -1}
            aria-controls={`${tab.id}-tab-content`}
            aria-selected={isActive}
            onClick={handleClick}
        >
            {tab.title}
        </button>
    );
};

/**
 * TabPanels - Renders all tab content panels
 */
const TabPanels = () => {
    const { tabs, maxScreenHeight, horizontal } = useTabsContext();

    return (
        <div className={classNames({ 'tw-basis-full': !horizontal, 'tw-min-h-0 tw-h-full': maxScreenHeight })}>
            {tabs.map((tab) => (
                <TabPanel key={tab.id} tab={tab} />
            ))}
        </div>
    );
};

/**
 * TabPanel - Individual tab content panel with lazy rendering
 */
const TabPanel = ({ tab }) => {
    const { activeTabId, shouldRenderTab, renderTabContent, maxScreenHeight } = useTabsContext();
    const isActive = activeTabId === tab.id;
    const shouldRender = shouldRenderTab(tab);

    return (
        <div
            id={`${tab.id}-tab-content`}
            role="tabpanel"
            aria-labelledby={`${tab.id}-tab`}
            hidden={!isActive}
            className={classNames({
                '!tw-hidden': !isActive,
                'tw-h-full': maxScreenHeight,
            })}
        >
            {shouldRender && !tab.disabled && renderTabContent(tab)}
        </div>
    );
};

/**
 * TabActions - Renders contextual actions for the active tab
 */
const TabActions = ({ actions: _actions = [], activeTab }) => {
    const filteredActions = useMemo(() => {
        if (!Array.isArray(_actions) || !activeTab) return [];

        return _actions.filter((action) => {
            if (action.for === undefined) return true;
            if (Array.isArray(action.for)) return action.for.includes(activeTab.id);
            return action.for === activeTab.id;
        });
    }, [_actions, activeTab]);

    if (filteredActions.length === 0) return null;

    return (
        <div className="tw-flex tw-justify-end tw-items-center tw-gap-2 tw-w-full">
            {filteredActions.map((action, index) => (
                <div key={`action-${index}-${Array.isArray(action.for) ? action.for.join('-') : action.for || 'all'}`}>
                    {typeof action.content === 'function' ? action.content() : action.content}
                </div>
            ))}
        </div>
    );
};

export default Tabs;
